import React, { Component, CSSProperties } from 'react';
import classnames from 'classnames';

import { PortalWrapper } from 'components/shared';

import styles from './Tooltip.module.scss';

type TooltipPosition =
  | 'Left'
  | 'Right'
  | 'BelowLeft'
  | 'BelowRight'
  | 'BelowCenter'
  | 'AboveLeft'
  | 'AboveRight'
  | 'AboveCenter';

interface TooltipProps extends StyleProps {
  bump?: boolean;
  content?: React.ReactNode;
  position?: TooltipPosition;
  onTooltipVisible?: () => void;
  onTooltipHidden?: () => void;
}

interface TooltipState {
  /** @any - Tooltip position data */
  style: any;
  /** @boolean - If the tooltip is anchored */
  anchored: boolean;
  isTooltipActive: boolean;
}

class Tooltip extends Component<TooltipProps, TooltipState> {
  state = {
    anchored: false,
    isTooltipActive: false,
    style: {},
  };

  anchor: React.MutableRefObject<HTMLDivElement | null> = React.createRef();

  componentDidMount() {
    this.setState({ anchored: true });
  }

  /**
   * Shows the tooltip
   */
  showTooltip = () => {
    this.setState({ isTooltipActive: true }, this.calculateStyle);
    this.props.onTooltipVisible && this.props.onTooltipVisible();
  };

  /** Hides the tooltip */
  hideTooltip = () => {
    this.setState({ isTooltipActive: false }, this.props.onTooltipHidden);
  };

  /**
   * Calculates the tooltip CSS position
   */
  calculateStyle = () => {
    const { position } = this.props;
    const boundingRect: any =
      this.anchor.current !== null ? this.anchor.current.getBoundingClientRect() : {};
    const { width, height, top, left } = boundingRect;

    let style: CSSProperties;

    if (position === 'Left') {
      style = {
        top: top - height / 2,
      };
    }

    if (position === 'Right') {
      style = {
        top: top - height / 2,
        left: left + width,
      };
    }

    if (position === 'BelowCenter') {
      style = {
        top: top + height,
        right: 'calc(100% - (' + left + 'px + (' + width + 'px / 2)))',
      };
    }

    if (position === 'BelowRight') {
      style = {
        top: top + height,
        left: left,
      };
    }

    if (position === 'BelowLeft') {
      style = {
        top: top + height,
        right: 'calc(100% - (' + left + 'px + ' + width + 'px))',
      };
    }

    if (position === 'AboveRight') {
      style = {
        top: top,
        left: left,
      };
    }

    if (position === 'AboveLeft') {
      style = {
        top: top,
        right: 'calc(100% - (' + left + 'px + ' + width + 'px))',
      };
    }

    if (position === 'AboveCenter') {
      style = {
        top: top,
        right: 'calc(100% - (' + left + 'px + (' + width + 'px / 2)))',
      };
    }

    this.setState((prevState) => ({
      ...prevState,
      style,
    }));
  };

  render() {
    const { isTooltipActive } = this.state;
    const { bump, className, children, content, position } = this.props;

    return (
      <div
        ref={this.anchor}
        className={classnames(className, styles.expando, {
          [styles.active]: isTooltipActive,
        })}
        onMouseLeave={this.hideTooltip}
      >
        {this.anchor.current && (
          <>
            <div
              className={styles.child}
              onMouseEnter={this.showTooltip}
              // onClick={this.clickHandler}
            >
              {children}
            </div>

            {isTooltipActive && (
              <PortalWrapper>
                <div
                  className={classnames(className, styles.container, {
                    [styles[`pos-${position}`]]: position,
                    [styles.bump]: bump,
                  })}
                  style={this.state.style}
                >
                  <div className={styles.tooltip}>{content}</div>
                </div>
              </PortalWrapper>
            )}
          </>
        )}
      </div>
    );
  }
}

export default Tooltip;
